import React, { useRef, useState, useEffect } from 'react';
import {
   Box,
   Button,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Typography,
   CircularProgress,
   Alert,
} from '@mui/material';
import { Download as DownloadIcon, Close as CloseIcon } from '@mui/icons-material';
import { Event } from '../../types/Event';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import EventFlyerLayout, { OrganizerProfile } from './EventFlyerLayout';

interface EventFlyerGeneratorProps {
   open: boolean;
   onClose: () => void;
   event: Event;
   organizerProfile?: OrganizerProfile | null;
}

/** Fetch image as blob and convert to data URL (for when Image+canvas is blocked by CORS). */
async function fetchImageAsDataUrl(url: string): Promise<string | null> {
   try {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) return null;
      const blob = await res.blob();
      return new Promise((resolve) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result as string);
         reader.onerror = () => resolve(null);
         reader.readAsDataURL(blob);
      });
   } catch {
      return null;
   }
}

/** Fetch image via server proxy (same-origin); returns data URL so html2canvas can draw it when CORS blocks client. */
async function fetchImageViaProxy(url: string): Promise<string | null> {
   try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      if (!apiUrl) return null;
      const res = await fetch(
         `${apiUrl}/api/utils/proxy-image?url=${encodeURIComponent(url)}`
      );
      if (!res.ok) return null;
      const blob = await res.blob();
      return new Promise((resolve) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result as string);
         reader.onerror = () => resolve(null);
         reader.readAsDataURL(blob);
      });
   } catch {
      return null;
   }
}

/** Convert image URL to data URL so html2canvas can draw it. Uses proxy first (same as server flyer), then client fallbacks. */
async function imageUrlToDataUrl(url: string): Promise<string | null> {
   const viaProxy = await fetchImageViaProxy(url);
   if (viaProxy) return viaProxy;
   const viaImage = await new Promise<string | null>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
         try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
               resolve(null);
               return;
            }
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
         } catch {
            resolve(null);
         }
      };
      img.onerror = () => resolve(null);
      img.src = url;
   });
   if (viaImage) return viaImage;
   return fetchImageAsDataUrl(url);
}

async function fetchOrganizerProfile(userId: string): Promise<OrganizerProfile | null> {
   try {
      let snap = await getDoc(doc(db, 'userProfiles', userId));
      if (!snap.exists()) snap = await getDoc(doc(db, 'userProfile', userId));
      if (!snap.exists()) snap = await getDoc(doc(db, 'users', userId));
      if (!snap.exists()) return null;
      const d = snap.data();
      return {
         name:
            d?.displayName ||
            d?.fullName ||
            (d?.firstName && d?.lastName ? `${d.firstName} ${d.lastName}` : '') ||
            d?.email ||
            'Event Host',
         photoURL:
            d?.photoURL ||
            d?.profilePicture ||
            d?.profilePhoto ||
            d?.avatar ||
            undefined,
         tagline: d?.tagline || undefined,
         bio: d?.bio || undefined,
      };
   } catch {
      return null;
   }
}

const FLYER_W = 1000;
const FLYER_H = 1056;
const PREVIEW_SCALE = 0.55; // fit full flyer in modal

const EventFlyerGenerator: React.FC<EventFlyerGeneratorProps> = ({
   open,
   onClose,
   event,
   organizerProfile: organizerProfileProp,
}) => {
   const flyerRef = useRef<HTMLDivElement>(null);
   const hostImageReadyResolveRef = useRef<(() => void) | null>(null);
   const [organizerProfile, setOrganizerProfile] = useState<OrganizerProfile | null>(organizerProfileProp ?? null);
   const [hostPhotoDataUrl, setHostPhotoDataUrl] = useState<string | null>(null);
   const [qrDataUrl, setQrDataUrl] = useState<string>('');
   const [loading, setLoading] = useState(true);
   const [exporting, setExporting] = useState(false);
   const [error, setError] = useState<string | null>(null);

   const publicEventUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/events/public/${event?.id}?source=qr_scan`;

   useEffect(() => {
      if (!open || !event) return;

      setError(null);
      setLoading(true);

      const run = async () => {
         try {
            const url = `${typeof window !== 'undefined' ? window.location.origin : ''}/events/public/${event.id}?source=qr_scan`;
            const dataUrl = await QRCode.toDataURL(url, {
               width: 300,
               margin: 2,
               color: { dark: '#000000', light: '#ffffff' },
            });
            setQrDataUrl(dataUrl);

            let profile: OrganizerProfile | null = null;
            if (organizerProfileProp != null) {
               profile = organizerProfileProp;
               setOrganizerProfile(organizerProfileProp);
            } else if (event.userId) {
               profile = await fetchOrganizerProfile(event.userId);
               setOrganizerProfile(profile);
            } else {
               setOrganizerProfile(null);
            }

            const photoUrl =
               profile?.photoURL || (profile as any)?.profilePicture || (profile as any)?.avatar;
            if (photoUrl) {
               const dataUrl = await imageUrlToDataUrl(photoUrl);
               setHostPhotoDataUrl(dataUrl ?? null);
            } else {
               setHostPhotoDataUrl(null);
            }
         } catch (e) {
            console.error(e);
            setError('Failed to load flyer data.');
         } finally {
            setLoading(false);
         }
      };

      run();
   }, [open, event?.id, event?.userId, organizerProfileProp]);

   const handleDownload = async () => {
      const el = flyerRef.current;
      if (!el || !event) return;

      setExporting(true);
      setError(null);

      try {
         const photoUrl =
            organizerProfile?.photoURL ||
            (organizerProfile as any)?.profilePicture ||
            (organizerProfile as any)?.avatar;
         if (photoUrl && !hostPhotoDataUrl) {
            const dataUrl = await imageUrlToDataUrl(photoUrl);
            if (dataUrl) setHostPhotoDataUrl(dataUrl);
         }

         // Wait for host image to be painted in the capture layout before html2canvas
         const hostImageReadyPromise = new Promise<void>((resolve) => {
            hostImageReadyResolveRef.current = () => {
               hostImageReadyResolveRef.current = null;
               resolve();
            };
         });
         await new Promise((r) => setTimeout(r, 200)); // let React commit and layout render
         await Promise.race([
            hostImageReadyPromise,
            new Promise<void>((_, reject) => setTimeout(() => reject(new Error('timeout')), 4000)),
         ]).catch(() => {}); // proceed even if timeout so export still runs

         const scale = 2;
         const w = el.scrollWidth;
         const h = el.scrollHeight;
         const canvas = await html2canvas(el, {
            scale,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#080A0F',
            logging: false,
            width: w,
            height: h,
         });

         const link = document.createElement('a');
         link.download = `${event.title?.toLowerCase().replace(/\s+/g, '-') || 'event'}-flyer.png`;
         link.href = canvas.toDataURL('image/png');
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
      } catch (err) {
         console.error(err);
         setError('Failed to export flyer. Please try again.');
      } finally {
         setExporting(false);
      }
   };

   const eyebrow = event?.eventType ? String(event.eventType).toUpperCase() : 'Event';

   return (
      <Dialog
         open={open}
         onClose={onClose}
         maxWidth="sm"
         fullWidth={false}
         PaperProps={{
            sx: { maxWidth: 520, maxHeight: '90vh' },
         }}
      >
         <DialogTitle sx={{ py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
               <Typography variant="h6">Event Flyer</Typography>
               <Button onClick={onClose} size="small">
                  <CloseIcon />
               </Button>
            </Box>
         </DialogTitle>

         <DialogContent sx={{ position: 'relative', maxHeight: 'calc(90vh - 140px)', overflow: 'auto', px: 2, display: 'flex', justifyContent: 'center' }}>
            {error && (
               <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
               </Alert>
            )}

            {loading ? (
               <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                  <CircularProgress />
                  <Typography>Loading flyer...</Typography>
               </Box>
            ) : (
               <>
                  {/* Scaled preview; scrollable when flyer grows with long content */}
                  <Box
                     sx={{
                        width: FLYER_W * PREVIEW_SCALE,
                        minHeight: FLYER_H * PREVIEW_SCALE,
                        maxHeight: '80vh',
                        flexShrink: 0,
                        overflow: 'auto',
                        overflowX: 'hidden',
                        borderRadius: 1,
                     }}
                  >
                     <Box
                        sx={{
                           width: FLYER_W,
                           minHeight: FLYER_H,
                           transform: `scale(${PREVIEW_SCALE})`,
                           transformOrigin: '0 0',
                        }}
                     >
                        <EventFlyerLayout
                           event={event}
                           organizerProfile={organizerProfile}
                           publicEventUrl={publicEventUrl}
                           qrDataUrl={qrDataUrl}
                           eyebrow={eyebrow}
                           hostPhotoDataUrl={hostPhotoDataUrl ?? undefined}
                        />
                     </Box>
                  </Box>
                  {/* Full-size copy for export (off-screen but painted so html2canvas can capture) */}
                  <Box
                     ref={flyerRef}
                     sx={{
                        position: 'absolute',
                        left: -FLYER_W - 500,
                        top: 0,
                        width: FLYER_W,
                        minHeight: FLYER_H,
                        pointerEvents: 'none',
                        zIndex: -1,
                     }}
                  >
                     <EventFlyerLayout
                        event={event}
                        organizerProfile={organizerProfile}
                        publicEventUrl={publicEventUrl}
                        qrDataUrl={qrDataUrl}
                        eyebrow={eyebrow}
                        hostPhotoDataUrl={hostPhotoDataUrl ?? undefined}
                        onHostImageReady={() => {
                           const fn = hostImageReadyResolveRef.current;
                           hostImageReadyResolveRef.current = null;
                           fn?.();
                        }}
                     />
                  </Box>
               </>
            )}
         </DialogContent>

         <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
               onClick={handleDownload}
               variant="contained"
               startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
               disabled={loading || exporting}
            >
               {exporting ? 'Exporting...' : 'Download / Print Flyer'}
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default EventFlyerGenerator;
